import { ShippingMethodDeliveryType } from "@core/types";
import { useTranslation } from "react-i18next";

import { useAuth } from "../../../contexts/auth-context";
import { useConfig } from "../../../providers/config";
import { useCartData } from "../../../providers/cartDataStore";
import { useMarketMode } from "../../../providers/marketMode";
import { useOrderData } from "../../../providers/orderDataStore";
import { useCustomerAddressesStorageData } from "../../../providers/customerAddressesStorage";

type RequiredToPlaceOrderCode =
  | "order_weight_exceeded"
  | "select_delivery_address"
  | "choose_delivery_time"
  | "express_delivery_weight_exceeded"
  | "select_payment_method"
  | "select_shipping_method"
  | "taxi_delivery_cost_not_calculated";

interface UseGetRequiredFieldsMessageParams {
  isTaxiMasterTurned: boolean;
}

export const useGetRequiredToPlaceOrderMessages = ({ isTaxiMasterTurned }: UseGetRequiredFieldsMessageParams) => {
  const { t } = useTranslation();
  const config = useConfig();
  const { mode } = useMarketMode();
  const { isUserAuthenticated } = useAuth();
  const { currentShippingAddressId } = useCustomerAddressesStorageData();
  const { getProductsTotalWeight } = useCartData();
  const { shippingMethod, paymentMethod, deliveryDate, deliveryInterval } = useOrderData();

  const totalWeight = getProductsTotalWeight();
  const isWeightExceeded = totalWeight > config.maxWeightForOrderKG!;
  const isExpressMaxWeightExceeded = mode === "express" && totalWeight > config.expressMaxWeightKg;

  const isDeliveryTimeRequired =
    mode === "market" &&
    !isTaxiMasterTurned &&
    shippingMethod?.methodDeliveryType !== ShippingMethodDeliveryType.nextDay &&
    (!deliveryDate || !deliveryInterval);

  const getRequiredToPlaceOrderMessages = () => {
    const requiredFields: { code: RequiredToPlaceOrderCode; message: string }[] = [];

    if (isWeightExceeded && !isTaxiMasterTurned) {
      requiredFields.push({
        code: "order_weight_exceeded",
        message: t("Order weight cannot exceed more", { max: config.maxWeightForOrderKG + " кг" }),
      });
    }

    if (!currentShippingAddressId)
      requiredFields.push({
        code: "select_delivery_address",
        message: t("Select delivery address"),
      });
    if (isDeliveryTimeRequired)
      requiredFields.push({ code: "choose_delivery_time", message: t("Choose delivery time") });
    if (isExpressMaxWeightExceeded) {
      requiredFields.push({
        message: t("Order amount for express delivery cannot exceed more", { max: config.expressMaxWeightKg + " кг" }),
        code: "express_delivery_weight_exceeded",
      });
    }
    if (!paymentMethod)
      requiredFields.push({
        code: "select_payment_method",
        message: t("Please, select the payment method"),
      });

    if (isUserAuthenticated && !shippingMethod) {
      requiredFields.push({
        code: "select_shipping_method",
        message: t("Please, select the shipping method"),
      });
    }

    if (isTaxiMasterTurned && (!shippingMethod?.cost || shippingMethod.cost === "0")) {
      requiredFields.push({
        code: "taxi_delivery_cost_not_calculated",
        message: t("Taxi delivery cost not calculated"),
      });
    }

    return requiredFields;
  };

  return { getRequiredToPlaceOrderMessages };
};
